import { useEffect, useState } from 'react';
import { api, fmt, type FloorStage } from '../api.ts';
import type { Route } from '../App.tsx';

interface Snapshot {
  date: string;
  producedToday: number;
  stagesWorkedToday: number;
  prePressItems: number;
  prePressQuantity: number;
  jobsInProduction: number;
  jobsAwaitingDispatch: number;
  quantityAwaitingDispatch: number;
  jobsShortOfBoard: number;
  totalOutstanding: number;
  totalDispatchedValue: number;
}

const rupees = (v: string | number | null | undefined) => `₹${fmt(v)}`;
const today = () => new Date().toISOString().slice(0, 10);

interface Row { key: string; label: string; count: number; detail: string; route: Route; alert?: boolean }

export default function Dashboard({ stages, go }: { stages: FloorStage[]; go: (route: Route) => void }) {
  const [s, setS] = useState<Snapshot | null>(null);
  const [date, setDate] = useState(today());
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
    api.get<Snapshot>(`/api/reports/snapshot?date=${date}`)
      .then(setS)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the dashboard'));
  }, [date]);

  const floor = stages.filter((st) => !st.is_dispatch && st.active);
  const late = stages.reduce((n, st) => n + st.late, 0);

  const rows: Row[] = s ? [
    { key: 'board', label: 'Jobs short of board', count: s.jobsShortOfBoard, detail: 'Board stock cannot cover them', route: 'stock:board', alert: true },
    { key: 'late', label: 'Late jobs', count: late, detail: 'Past their due date on the floor', route: 'production', alert: true },
    { key: 'prepress', label: 'Waiting in pre-press', count: s.prePressItems, detail: `${fmt(s.prePressQuantity)} pieces`, route: 'prepress' },
    { key: 'dispatch', label: 'Ready to dispatch', count: s.jobsAwaitingDispatch, detail: `${fmt(s.quantityAwaitingDispatch)} pieces`, route: 'dispatch:queue' },
  ] : [];

  return (
    <div className="dash">
      <div className="dash-head">
        <div>
          <h2>Dashboard</h2>
          <p>Where everything stands today</p>
        </div>
        <label className="dash-day">
          Day
          <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value || today())} />
        </label>
      </div>

      {error && <div className="error" style={{ marginBottom: 16 }}>{error}</div>}
      {!s && !error && <div className="empty">Loading…</div>}

      {s && (
        <>
          <section className="dash-sec">
            <h3>Needs attention</h3>
            <div className="dash-list">
              {rows.map((r) => (
                <button key={r.key} className={`dash-row${r.count > 0 ? '' : ' none'}`} onClick={() => go(r.route)}>
                  <span className={`dash-n${r.count > 0 && r.alert ? ' alert' : ''}`}>{r.count}</span>
                  <span className="dash-label">{r.label}</span>
                  <span className="dash-detail">{r.count > 0 ? r.detail : 'Nothing waiting'}</span>
                  <span className="dash-go">›</span>
                </button>
              ))}
              <button className={`dash-row${s.totalOutstanding > 0 ? '' : ' none'}`} onClick={() => go('payments:outstanding')}>
                <span className="dash-n money">{rupees(s.totalOutstanding)}</span>
                <span className="dash-label">Due to us</span>
                <span className="dash-detail">{s.totalOutstanding > 0 ? 'Before GST' : 'Nothing due'}</span>
                <span className="dash-go">›</span>
              </button>
            </div>
          </section>

          <section className="dash-sec">
            <h3>Factory flow</h3>
            {floor.length === 0 ? (
              <div className="dash-list"><div className="dash-empty">No stages set up.</div></div>
            ) : (
              <div className="dash-flow">
                {floor.map((st) => (
                  <button key={st.id} className="dash-stage" onClick={() => go('production')}>
                    <span className="dash-stage-name">{st.name}</span>
                    <span className="dash-stage-n">{st.jobs}</span>
                    <span className={`dash-stage-sub${st.late > 0 ? ' alert' : ''}`}>
                      {st.late > 0 ? `${st.late} late` : st.jobs === 1 ? 'job' : 'jobs'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="dash-sec">
            <h3>Today</h3>
            <div className="dash-figs">
              <div>
                <span>Produced on this day</span>
                <strong>{fmt(s.producedToday)}</strong>
                <em>{s.stagesWorkedToday} stage entr{s.stagesWorkedToday === 1 ? 'y' : 'ies'}</em>
              </div>
              <div>
                <span>Jobs in production</span>
                <strong>{s.jobsInProduction}</strong>
                <em>on the floor now</em>
              </div>
              <div>
                <span>Shipped to date</span>
                <strong>{rupees(s.totalDispatchedValue)}</strong>
                <em>before GST</em>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
